/**
 * ScoringWeights.tsx
 *
 * Admin-only panel rendered inside the Admin tab when adminTab === 'scoring'.
 * Receives token as a prop — same pattern as everything else in Dashboard.tsx.
 *
 * Shows each scoring criterion as a slider + numeric input combo.
 * The key UX insight: we show "effective %" (normalized weight) next to the
 * raw number, so admins immediately understand the real impact of their
 * changes rather than wondering why "weight 50" doesn't mean "50%".
 */

import React, { useEffect, useState, useCallback } from 'react';

interface ScoringWeightsProps {
  token: string;
}

interface ScoringCriterion {
  id: number;
  criterion_key: string;
  criterion_name: string;
  weight: number;
  description: string;
  active: boolean;
}

const API = 'https://coast-terminal-production.up.railway.app';

// Each criterion in the distribution bar gets one of these colors, cycling if > 6 criteria
const BAR_COLORS = ['#1A5A8A', '#7C5CBF', '#C9A96E', '#1A7A4A', '#2A8A8A', '#8A5A1A'];

const ScoringWeights: React.FC<ScoringWeightsProps> = ({ token }) => {
  // Design tokens — matching Dashboard.tsx (cream/navy/gold) so this
  // panel visually belongs to the admin area instead of appearing as a
  // dark pop-out. Swapped 2026-04-17 from the older dark-navy palette.
  const gold = '#C9A96E';
  const green = '#1A7A4A';
  const red = '#CC2200';
  const bg = '#ffffff';
  const surface = '#fafaf8';
  const surfaceRaised = '#f2ede6';
  const surfaceHigh = '#f2ede6';
  const border = '#c4a870';
  const borderLight = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';

  const headers = { Authorization: `Bearer ${token}` };

  const [criteria, setCriteria] = useState<ScoringCriterion[]>([]);
  const [original, setOriginal] = useState<ScoringCriterion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchWeights = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/scoring-weights/`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCriteria(data.weights);
      setOriginal(data.weights);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchWeights(); }, [fetchWeights]);

  // ── Computed values ────────────────────────────────────────────────────────

  // Total of all active weights — used to normalize to a percentage.
  // This is what the scoring algorithm divides by at compute time.
  const totalWeight = criteria.filter(c => c.active).reduce((sum, c) => sum + c.weight, 0);

  // The actual percentage contribution of each criterion after normalization.
  // An admin who sets weights to [50, 50, 25, 25] gets the same result as [2, 2, 1, 1].
  // Showing this value makes that obvious.
  const effectivePct = (c: ScoringCriterion): number =>
    (!c.active || totalWeight === 0) ? 0 : (c.weight / totalWeight) * 100;

  // Only show "Save" as active when something has actually changed
  const hasChanges =
    JSON.stringify(criteria.map(c => ({ k: c.criterion_key, w: c.weight, a: c.active }))) !==
    JSON.stringify(original.map(c => ({ k: c.criterion_key, w: c.weight, a: c.active })));

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleChange = (key: string, field: 'weight' | 'active', value: number | boolean) => {
    setCriteria(prev => prev.map(c => c.criterion_key === key ? { ...c, [field]: value } : c));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/scoring-weights/bulk`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weights: criteria.map(c => ({
            criterion_key: c.criterion_key,
            weight: c.weight,
            active: c.active,
          })),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setOriginal([...criteria]);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      setError(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Shared style objects ───────────────────────────────────────────────────

  const sl: React.CSSProperties = { fontSize: '9px', color: gold, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '6px', fontWeight: 700 };
  const numInput: React.CSSProperties = { backgroundColor: surfaceHigh, border: `1px solid ${borderLight}`, borderRadius: '2px', padding: '7px 10px', color: textPrimary, fontSize: '12px', outline: 'none', fontFamily: 'inherit', width: '60px', textAlign: 'center', boxSizing: 'border-box' };
  const saveBtnActive: React.CSSProperties = { backgroundColor: gold, color: bg, border: 'none', borderRadius: '2px', padding: '10px 28px', fontWeight: 800, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' };
  const saveBtnDisabled: React.CSSProperties = { ...saveBtnActive, backgroundColor: surfaceHigh, color: textMuted, cursor: 'default' };
  const outBtn: React.CSSProperties = { backgroundColor: 'transparent', color: textSecondary, border: `1px solid ${borderLight}`, borderRadius: '2px', padding: '10px 18px', fontWeight: 700, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return <div style={{ color: textMuted, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', padding: '20px 0' }}>Loading weights...</div>;
  }

  const activeCriteria = criteria.filter(c => c.active);

  return (
    <div style={{ maxWidth: '780px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={sl}>Scoring Configuration</div>
        <div style={{ fontSize: '22px', fontWeight: 900, color: textPrimary, letterSpacing: '-0.5px', marginBottom: '4px' }}>Deal Scoring Weights</div>
        <div style={{ fontSize: '12px', color: textMuted, lineHeight: 1.7 }}>
          Weights are normalized at compute time — the ratios between them matter, not the absolute values.
          A weight of 50 with total 200 is identical to a weight of 25 with total 100.
          The "effective %" column shows what each criterion actually contributes to the score.
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', backgroundColor: 'rgba(204,34,0,0.1)', border: `1px solid rgba(204,34,0,0.3)`, color: red, fontSize: '11px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Distribution bar — visual summary of the current weight allocation */}
      {activeCriteria.length > 0 && (
        <div style={{ backgroundColor: surface, border: `1px solid ${border}`, padding: '16px 20px', marginBottom: '20px' }}>
          <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '10px' }}>
            Weight Distribution — {activeCriteria.length} active criteria
          </div>
          {/* The flex bar — each segment's width = its effective percentage */}
          <div style={{ display: 'flex', height: '7px', gap: '1px', marginBottom: '12px', backgroundColor: surfaceHigh }}>
            {activeCriteria.map((c, i) => {
              const pct = effectivePct(c);
              return (
                <div
                  key={c.criterion_key}
                  title={`${c.criterion_name}: ${pct.toFixed(1)}%`}
                  style={{ flex: pct, backgroundColor: BAR_COLORS[i % BAR_COLORS.length], transition: 'flex 0.3s', minWidth: pct > 0 ? '2px' : '0' }}
                />
              );
            })}
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {activeCriteria.map((c, i) => (
              <div key={c.criterion_key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '7px', height: '7px', backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }} />
                <div style={{ fontSize: '10px', color: textSecondary }}>
                  {c.criterion_name} <span style={{ color: textMuted }}>({effectivePct(c).toFixed(0)}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Criteria rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
        {criteria.map(c => {
          const pct = effectivePct(c);
          return (
            <div
              key={c.criterion_key}
              style={{
                backgroundColor: c.active ? surface : bg,
                border: `1px solid ${border}`,
                borderLeft: `2px solid ${c.active ? gold : border}`,
                padding: '15px 20px',
                opacity: c.active ? 1 : 0.5,
                transition: 'opacity 0.2s, background-color 0.2s',
              }}
            >
              {/* Row header: name + effective % badge + active toggle */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: c.active ? '12px' : 0 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '3px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: textPrimary }}>{c.criterion_name}</div>
                    {c.active && (
                      <div style={{ fontSize: '9px', color: textMuted, backgroundColor: surfaceHigh, border: `1px solid ${border}`, padding: '1px 7px', fontFamily: 'monospace' }}>
                        {pct.toFixed(1)}% effective
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: textMuted, lineHeight: 1.6 }}>{c.description}</div>
                </div>

                {/* Active toggle — styled like a switch */}
                <div
                  onClick={() => handleChange(c.criterion_key, 'active', !c.active)}
                  style={{ width: '36px', height: '20px', borderRadius: '10px', backgroundColor: c.active ? gold : surfaceHigh, border: `1px solid ${c.active ? gold : borderLight}`, cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background-color 0.2s', marginTop: '2px' }}
                  title={c.active ? 'Disable criterion' : 'Enable criterion'}
                >
                  <div style={{ position: 'absolute', top: '2px', width: '14px', height: '14px', borderRadius: '7px', backgroundColor: c.active ? bg : textMuted, left: c.active ? '19px' : '2px', transition: 'left 0.2s' }} />
                </div>
              </div>

              {/* Slider + numeric input + fill bar — only shown when active */}
              {c.active && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <input
                      type="range"
                      min="0" max="100"
                      value={c.weight}
                      onChange={e => handleChange(c.criterion_key, 'weight', Number(e.target.value))}
                      style={{ flex: 1, accentColor: gold, cursor: 'pointer' }}
                    />
                    <input
                      type="number"
                      min="0" max="100"
                      value={c.weight}
                      onChange={e => handleChange(c.criterion_key, 'weight', Math.min(100, Math.max(0, Number(e.target.value))))}
                      style={numInput}
                    />
                  </div>
                  {/* Effective weight fill bar — the actual visual signal of impact */}
                  <div style={{ height: '3px', backgroundColor: surfaceHigh, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${pct}%`,
                      backgroundColor: pct > 30 ? gold : '#1A5A8A',
                      transition: 'width 0.3s',
                    }} />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', borderTop: `1px solid ${border}` }}>
        <button
          onClick={() => { setCriteria([...original]); setSaved(false); }}
          disabled={!hasChanges || saving}
          style={{ ...outBtn, opacity: hasChanges && !saving ? 1 : 0.3, cursor: hasChanges && !saving ? 'pointer' : 'default' }}
        >
          Reset Changes
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {saved && <span style={{ fontSize: '11px', color: green, fontWeight: 700, letterSpacing: '0.5px' }}>✓ Saved</span>}
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            style={hasChanges && !saving ? saveBtnActive : saveBtnDisabled}
          >
            {saving ? 'Saving...' : 'Save Weights'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoringWeights;